import { TFunction } from 'i18next'
import { TRANSLATION_KEYS } from 'locales/translation-keys'
import { checkRoutePermissions } from 'utils/authentication'
import { useMPMEnv } from 'utils/env'

export interface NavigationOption {
    value: string
    label: string
    path: string
}

/**
 * Get all available navigation options for landing page selection
 */
export const getAllNavigationOptions = (t: TFunction): NavigationOption[] => [
    {
        value: '/sales/overview',
        label: t(TRANSLATION_KEYS.sales.title),
        path: '/sales',
    },
    {
        value: '/payroll/monthly',
        label: t(TRANSLATION_KEYS.payroll.title),
        path: '/payroll',
    },
    {
        value: '/safety/lagging',
        label: t(TRANSLATION_KEYS.safety.title),
        path: '/safety',
    },
    {
        value: '/shortage',
        label: t(TRANSLATION_KEYS.shortage.title),
        path: '/shortage',
    },
    {
        value: '/assets-protection/payroll',
        label: t(TRANSLATION_KEYS.assetsProtection.title),
        path: '/assets-protection',
    },
    {
        value: '/loyalty/overview',
        label: t(TRANSLATION_KEYS.loyalty.title),
        path: '/loyalty',
    },
    {
        value: '/trailer',
        label: t(TRANSLATION_KEYS.trailer.title),
        path: '/trailer',
    },
    {
        value: '/brands',
        label: t(TRANSLATION_KEYS.brands.title),
        path: '/brands',
    },
    {
        value: '/store-fulfillment/pickup',
        label: t(TRANSLATION_KEYS.storeFulfillment.title),
        path: '/store-fulfillment',
    },
    {
        value: '/operations',
        label: t(TRANSLATION_KEYS.operationalMetrics.title),
        path: '/operations',
    },
    {
        value: '/store-facts',
        label: t(TRANSLATION_KEYS.storeFacts.title),
        path: '/store-facts',
    },
    {
        value: '/notes',
        label: t(TRANSLATION_KEYS.notes.notes),
        path: '/notes',
    },
    {
        value: '/item-search',
        label: t(TRANSLATION_KEYS.itemSearch.title),
        path: '/item-search',
    },
    {
        value: '/mytarget-performance/guest',
        label: t(TRANSLATION_KEYS.myTargetPerformance.title),
        path: '/mytarget-performance',
    },
]

/**
 * Filter navigation options based on user permissions and location type
 */
export const getAvailableNavigationOptions = ({
    allOptions,
    env,
    greenfieldGroups,
    locationType,
}: {
    allOptions: NavigationOption[]
    env: ReturnType<typeof useMPMEnv>
    greenfieldGroups: string[] | null
    locationType: string
}): NavigationOption[] => {
    const { permissionsByKPI } = env

    if (!greenfieldGroups || !permissionsByKPI) {
        return []
    }

    const isStoreLevel = locationType === 'STR'

    const filtered = allOptions.filter((option) => {
        // Filter out store-only pages if not at store level
        if (
            !isStoreLevel &&
            (option.path === '/notes' ||
                option.path === '/store-facts' ||
                option.path === '/item-search' ||
                option.path === '/insights')
        ) {
            return false
        }

        const hasPermission = checkRoutePermissions({
            path: option.path,
            userPermissions: greenfieldGroups,
            permissionsByKPI,
        })
        return hasPermission
    })

    // Return filtered options, or all options if filter result is empty
    return filtered.length > 0 ? filtered : allOptions
}
